import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ProductCatalogHttpService } from './product-catalog.http.service';
import { isTariffExpiredError } from './product-catalog.helper';
import ProductCatalogActions from '@web/store/website/product-catalog/product-catalog.actions';
import type { CatalogProductCard } from '@web/widgets/product-card/type/product-card-widget.type';

@Injectable()
export class ProductCatalogEffects {
    private readonly actions$ = inject(Actions);
    private readonly productCatalogHttpService = inject(ProductCatalogHttpService);

    public readonly loadCatalogContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ProductCatalogActions.loadCatalogContent),
            switchMap(() => {
                return forkJoin([
                    this.productCatalogHttpService.getUserCategories(),
                    this.productCatalogHttpService.getAllCatalogProducts(),
                ]).pipe(
                    mergeMap(([categories, { products, total }]) => [
                        ProductCatalogActions.loadCategoriesSuccess({ categories }),
                        ProductCatalogActions.loadProductsSuccess({ products, total }),
                    ]),
                    catchError((error) => [
                        ProductCatalogActions.loadCategoriesFailure({ error }),
                        ProductCatalogActions.loadProductsFailure({ error }),
                        ProductCatalogActions.setTariffExpired({ isTariffExpired: isTariffExpiredError(error) }),
                    ])
                );
            })
        );
    });

    public readonly loadCategoriesRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductCatalogActions.loadCategoriesRequest),
            switchMap(() =>
                this.productCatalogHttpService.getUserCategories().pipe(
                    map((categories: any[]) => ProductCatalogActions.loadCategoriesSuccess({ categories })),
                    catchError((error) => [ProductCatalogActions.loadCategoriesFailure({ error })])
                )
            )
        )
    );

    public readonly loadProductsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductCatalogActions.loadProductsRequest),
            switchMap(({ categoryId, search, offset, limit }) =>
                this.productCatalogHttpService.getAllCatalogProducts({ categoryId, search, offset, limit }).pipe(
                    map(({ products, total }: { products: CatalogProductCard[]; total: number }) =>
                        ProductCatalogActions.loadProductsSuccess({ products, total })
                    ),
                    catchError((error) => [
                        ProductCatalogActions.loadProductsFailure({ error }),
                        ProductCatalogActions.setTariffExpired({ isTariffExpired: isTariffExpiredError(error) }),
                    ])
                )
            )
        )
    );

    public readonly loadProductsByCategory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductCatalogActions.setCategory),
            switchMap(({ category }) =>
                this.productCatalogHttpService.getAllCatalogProducts({ categoryId: category }).pipe(
                    map(({ products, total }: { products: CatalogProductCard[]; total: number }) =>
                        ProductCatalogActions.loadProductsSuccess({ products, total })
                    ),
                    catchError((error) => [
                        ProductCatalogActions.loadProductsFailure({ error }),
                        ProductCatalogActions.setTariffExpired({ isTariffExpired: isTariffExpiredError(error) }),
                    ])
                )
            )
        )
    );
}
